// "This is wrong" - said from the search, about the world you mean it in.
//
// The decision this modal exists to force is the SOURCE. An issue raised
// against a sheet is answered by a reissued sheet; one raised against
// the model is answered by a push. Get that wrong at creation and the
// issue asks for the wrong deliverable for the rest of its life, so the
// choice is made deliberately here rather than inferred from whichever
// pane happened to be clicked.
//
// Which hit is offered per world is also a choice: a query returns many,
// and the one that was clicked is only a default. Both selects list
// every hit that world returned, so a coordinator can raise the issue
// against the right sheet without going back and re-searching.
import React, { useEffect, useMemo, useState } from "react";
import {
  Alert, DatePicker, Form, Input, InputNumber, Modal, Radio, Select, Space, Tag, Typography, message,
} from "antd";
import {
  createIssueFromSearch, describeSearchIssue, hitLabel, sheetSnapshot, suggestTitle,
} from "helpers/issueFromSearch";
import { matchOption } from "components/SearchSelect/searchSelect";

const { Text } = Typography;

export default function RaiseIssueModal({
  open, onClose, projectId, query, hits2d = [], hits3d = [], agreed = [],
  // What was clicked, and which pane it was clicked in.
  seed, seedWorld,
  // The page raster on screen, and WHICH page it is. Both are needed:
  // the hit chosen in this modal is not necessarily the hit the canvas
  // is showing, and a picture of the wrong sheet with this hit's box
  // drawn on it is worse than no picture at all.
  sheetImgRef, activeSheet,
  onCreated,
}) {
  const [form] = Form.useForm();
  const [source, setSource] = useState("2d");
  const [id2d, setId2d] = useState(null);
  const [id3d, setId3d] = useState(null);
  const [busy, setBusy] = useState(false);
  const [touchedText, setTouchedText] = useState(false);

  const has2d = hits2d.length > 0;
  const has3d = hits3d.length > 0;

  // Opening resets everything to what is on screen right now. A modal
  // that remembered the last issue's wording would quietly attach it to
  // a different search.
  useEffect(() => {
    if (!open) return;
    const s2 = seedWorld === "2d" && seed ? seed : hits2d[0] || null;
    const s3 = seedWorld === "3d" && seed ? seed : hits3d[0] || null;
    setId2d(s2 ? String(s2._id) : null);
    setId3d(s3 ? String(s3._id) : null);
    // Default to the world the click came from, and only offer "both"
    // as a default when the click itself was on an agreed hit.
    const start = seedWorld && ((seedWorld === "2d" && has2d) || (seedWorld === "3d" && has3d))
      ? seedWorld
      : (has2d ? "2d" : "3d");
    setSource(seed && seed.agreed && has2d && has3d ? "hybrid" : start);
    setTouchedText(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const hit2d = useMemo(
    () => hits2d.find((h) => String(h._id) === String(id2d)) || null, [hits2d, id2d]
  );
  const hit3d = useMemo(
    () => hits3d.find((h) => String(h._id) === String(id3d)) || null, [hits3d, id3d]
  );

  // The wording follows the choice until the moment it is edited by
  // hand, after which it is left alone - retyping over someone's words
  // because they changed a radio button is worse than a stale default.
  useEffect(() => {
    if (!open || touchedText) return;
    form.setFieldsValue({
      title: suggestTitle({ query, source, hit2d, hit3d }),
      description: describeSearchIssue({ query, source, hit2d, hit3d, agreed }),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, source, hit2d, hit3d, touchedText]);

  // Whether the canvas is actually showing the sheet the issue is about.
  const showingThisHit = !!(hit2d && activeSheet
    && String(activeSheet.docId) === String(hit2d.docId)
    && activeSheet.page === hit2d.page);

  const snapshot = () => {
    // Only the 2D pane has pixels to capture; the 3D pane in split
    // search is a list of elements, not a viewer, so a 3D-only issue is
    // created without a picture rather than with a misleading one.
    if (source === "3d") return "";
    // And only when those pixels are the right sheet.
    if (!showingThisHit) return "";
    return sheetSnapshot(sheetImgRef && sheetImgRef.current, hit2d);
  };

  const submit = async () => {
    let v;
    try { v = await form.validateFields(); } catch (e) { return; }
    if (source !== "3d" && !hit2d) { message.error("Pick the sheet hit this is about"); return; }
    if (source !== "2d" && !hit3d) { message.error("Pick the model hit this is about"); return; }
    setBusy(true);
    try {
      const r = await createIssueFromSearch({
        projectId, source, hit2d, hit3d, query, agreed,
        title: v.title,
        description: v.description,
        estimatedHours: v.estimatedHours,
        priority: v.priority || "normal",
        dueDate: v.dueDate ? v.dueDate.toISOString() : undefined,
        snapshot: snapshot(),
      });
      message.success(
        r.snapshotFailed
          ? "Issue created. The snapshot could not be attached."
          : "Issue created."
      );
      if (onCreated) onCreated(r);
      if (onClose) onClose();
    } catch (e) {
      message.error(e.message || "Could not create the issue");
    } finally {
      setBusy(false);
    }
  };

  const optionsFor = (hits, world) => hits.map((h) => ({
    value: String(h._id),
    label: hitLabel(h, world) || "(no text)",
  }));

  return (
    <Modal
      open={open}
      onCancel={onClose}
      onOk={submit}
      okText="Create issue"
      confirmLoading={busy}
      width={720}
      title="Raise an issue from this result"
      destroyOnClose
    >
      <Form form={form} layout="vertical" initialValues={{ priority: "normal" }}>
        <Form.Item label="Raise it against">
          <Radio.Group value={source} onChange={(e) => setSource(e.target.value)}>
            <Radio.Button value="2d" disabled={!has2d}>The sheet (2D)</Radio.Button>
            <Radio.Button value="3d" disabled={!has3d}>The model (3D)</Radio.Button>
            <Radio.Button value="hybrid" disabled={!has2d || !has3d}>Both</Radio.Button>
          </Radio.Group>
          <div style={{ fontSize: 12, color: "#888", marginTop: 6 }}>
            {source === "2d" && "Answered by a reissued sheet. The issue points at the Doc Control record."}
            {source === "3d" && "Answered by a model push. The issue carries the element's uniqueId."}
            {source === "hybrid" && "Carries both references. It does not claim they are the same object."}
          </div>
        </Form.Item>

        {source !== "3d" && (
          <Form.Item label="Sheet hit">
            <Select filterOption={matchOption}
              showSearch optionFilterProp="label"
              value={id2d} onChange={setId2d}
              options={optionsFor(hits2d, "2d")}
              placeholder={has2d ? "Pick a sheet hit" : "Nothing on the sheets matched"}
              disabled={!has2d}
            />
          </Form.Item>
        )}

        {source !== "2d" && (
          <Form.Item label="Model hit">
            <Select filterOption={matchOption}
              showSearch optionFilterProp="label"
              value={id3d} onChange={setId3d}
              options={optionsFor(hits3d, "3d")}
              placeholder={has3d ? "Pick a model hit" : "Nothing in the model matched"}
              disabled={!has3d}
            />
          </Form.Item>
        )}

        {source === "hybrid" && !!agreed.length && (
          <Alert
            type="info" showIcon style={{ marginBottom: 16 }}
            message={<span>Both worlds returned <Tag color="green">{agreed.join(" ")}</Tag></span>}
            description="That is agreement between two indexes, not proof the sheet text and the model element are the same object. The issue says so too."
          />
        )}

        <Form.Item
          name="title" label="Title"
          rules={[{ required: true, message: "An issue needs a title" }]}
        >
          <Input onChange={() => setTouchedText(true)} maxLength={200} />
        </Form.Item>

        <Form.Item name="description" label="Description">
          <Input.TextArea rows={7} onChange={() => setTouchedText(true)} />
        </Form.Item>

        {/* Required on every new issue (owner, 2026-10-16). */}
        <Form.Item
          name="estimatedHours" label="Estimated hours"
          rules={[{ required: true, message: "Enter the estimated hours" }]}
        >
          <InputNumber aria-label="Estimated hours" min={0.25} step={0.25} precision={2} addonAfter="hours" style={{ width: 200 }} />
        </Form.Item>

        <Space size={16} align="start" style={{ display: "flex" }}>
          <Form.Item name="priority" label="Priority" style={{ minWidth: 160 }}>
            <Select options={[
              { value: "urgent", label: "Urgent" },
              { value: "normal", label: "Normal" },
              { value: "low", label: "Low" },
            ]} />
          </Form.Item>
          <Form.Item name="dueDate" label="Due date">
            <DatePicker style={{ width: 200 }} />
          </Form.Item>
        </Space>

        {source !== "3d" && (
          <Text type="secondary" style={{ fontSize: 12 }}>
            {showingThisHit
              ? "A snapshot of the hit in context on the sheet is attached automatically."
              : "No snapshot: the pane is showing a different sheet. Select this hit in the 2D pane first if you want a picture on the issue."}
          </Text>
        )}
      </Form>
    </Modal>
  );
}
